import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ClipboardList, Users, Briefcase, MessageSquare, ArrowRight, Sparkles, FileText, BarChart3 } from "lucide-react";

export default function CompanyOverview() {
  const { user, profile } = useAuth();
  const [shortlists, setShortlists] = useState([]);
  const [projects, setProjects] = useState([]);
  const [requests, setRequests] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, p, r, m, n] = await Promise.all([
          api.get("/shortlists/me"), api.get("/projects/"), api.get("/assessments/"),
          api.get("/meetings/"), api.get("/notifications/"),
        ]);
        setShortlists(s.data || []);
        setProjects(p.data || []);
        setRequests(r.data || []);
        setMeetings(m.data || []);
        setNotifs((n.data || []).slice(0, 5));
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  const submitted = requests.filter(r => r.status === "submitted").length;
  const stats = [
    { label: "Check-up inviati", value: submitted, icon: ClipboardList, link: "/app/requests" },
    { label: "Shortlist disponibili", value: shortlists.length, icon: Users, link: "/app/shortlist" },
    { label: "Progetti attivi", value: projects.filter(p => p.status === "active").length, icon: Briefcase, link: "/app/projects" },
    { label: "Incontri in agenda", value: meetings.filter(m => m.status === "scheduled").length, icon: MessageSquare, link: "/app/meetings" },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Benvenuto, {user?.full_name?.split(" ")[0]}</h1>
        <p className="mt-1 text-slate-600">{profile?.company_name || ""} · Ecco una panoramica della tua attività.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <Link to={s.link} key={i}>
              <div className="cxo-card p-5 hover:-translate-y-0.5 transition-transform">
                <div className="flex items-center gap-2 text-slate-500">
                  <Icon size={16} /><span className="text-xs uppercase tracking-wider font-semibold">{s.label}</span>
                </div>
                <p className="mt-2 text-3xl font-display font-bold cxo-tabular text-slate-900">{s.value}</p>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 cxo-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold text-slate-900">Azioni consigliate</h2>
          </div>
          {requests.length === 0 ? (
            <Link to="/checkup" className="block p-5 rounded-lg border-2 border-dashed border-[#2D6BFF]/30 hover:bg-[#EAF0FF]/30">
              <div className="flex items-center gap-3">
                <Sparkles className="text-[#2D6BFF]" size={20} />
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">Inizia il tuo check-up gratuito</p>
                  <p className="text-sm text-slate-600">Pochi minuti per identificare il manager più utile.</p>
                </div>
                <ArrowRight size={18} className="text-[#2D6BFF]" />
              </div>
            </Link>
          ) : shortlists.length > 0 ? (
            <Link to="/app/shortlist" className="block p-5 rounded-lg bg-[#E9F8F1] hover:bg-[#dff5e8]">
              <div className="flex items-center gap-3">
                <Users className="text-[#1F9D6A]" size={20} />
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">Hai una shortlist pronta!</p>
                  <p className="text-sm text-slate-700">Esplora i manager selezionati per te.</p>
                </div>
                <ArrowRight size={18} className="text-[#1F9D6A]" />
              </div>
            </Link>
          ) : (
            <div className="p-5 rounded-lg bg-amber-50 text-amber-800">
              <p className="font-semibold">La tua richiesta è in lavorazione</p>
              <p className="text-sm mt-1">Il team CXOORA sta selezionando manager compatibili con il tuo bisogno.</p>
            </div>
          )}

          <h3 className="mt-6 mb-3 text-sm font-semibold text-slate-700">Le tue richieste recenti</h3>
          <div className="space-y-2">
            {requests.slice(0, 3).map(r => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div>
                  <p className="font-medium text-slate-900 text-sm">{r.recommended_role || "Check-up"}</p>
                  <p className="text-xs text-slate-500">Stato: {r.status}</p>
                </div>
                <Badge className="bg-white text-slate-700 border-slate-200">{(r.priority_level || "medium")}</Badge>
              </div>
            ))}
            {!requests.length && <p className="text-sm text-slate-500">Nessuna richiesta. Inizia dal <Link to="/checkup" className="text-[#2D6BFF] font-semibold">check-up</Link>.</p>}
          </div>
        </div>

        <div className="cxo-card p-6">
          <h2 className="font-display text-lg font-semibold text-slate-900">Notifiche recenti</h2>
          <div className="mt-3 space-y-2">
            {notifs.length ? notifs.map(n => (
              <div key={n.id} className="p-3 rounded-lg bg-slate-50 text-sm">
                <p className="font-semibold text-slate-900">{n.title}</p>
                <p className="text-xs text-slate-600 mt-0.5">{n.body}</p>
              </div>
            )) : <p className="text-sm text-slate-500">Nessuna notifica</p>}
          </div>
          <Link to="/app/notifications" className="mt-3 inline-flex items-center text-sm text-[#2D6BFF] font-semibold">Vedi tutte <ArrowRight size={14} className="ml-1" /></Link>
        </div>
      </div>
    </div>
  );
}
